'use client'

import React, { useDeferredValue, useEffect, useMemo, useState } from 'react'

import { IDBTable } from '@/core/db'
import { useCompContext } from '@/context/__project__/CompContext'
import { EVENT_KEYS } from '@/shared/custom-event'
import { isEmpty, isFile } from '@/shared/utils'

export interface BaseCompFormProps {
  initialData?: Record<string, any>
  showDebug?: boolean
}

export interface CompFormProps extends BaseCompFormProps {
  settings: Record<string, any>
  dataSource: string
  dataSourceTable: IDBTable
  children: (props: any) => React.ReactNode
}

/**
 * CompFormCore
 */
export const CompFormCore = React.memo(
  ({ children, initialData, showDebug, settings, dataSource, dataSourceTable }: CompFormProps) => {
    const { component } = useCompContext()

    // form states
    const [formState, setFormState] = useState<Record<string, unknown>>(() => {
      if (isEmpty(initialData)) return {}
      return initialData ?? {}
    })

    const [requiredFieldsStatus, setRequiredFieldsStatus] = useState<Record<string, boolean>>({})
    const [formErrors, setFormErrors] = useState<Record<string, string | undefined>>({})

    // debug values
    const formValues = useDeferredValue(formState)
    const formattedValues = useMemo(() => {
      return Object.entries(formValues).reduce((acc: any, [key, value]) => {
        const col = dataSourceTable?.columnsMap.get(key)
        const colName = col?._Name || key
        acc[colName] = isFile(value) ? `[File] ${value.name}` : value
        return acc
      }, {})
    }, [formValues, dataSourceTable])

    useEffect(() => {
      const handleFormReset = (e: Event) => {
        const { formId } = (e as CustomEvent).detail

        if (formId === component._id) {
          setFormState({})
          setFormErrors({})
          setRequiredFieldsStatus({})

          // fire event to recalculate hidden field default value
          window.dispatchEvent(new CustomEvent(EVENT_KEYS.BUILDER_FORM_RESET_HIDDEN_FIELD, { detail: { formId } }))
        }
      }

      window.addEventListener(EVENT_KEYS.BUILDER_FORM_RESET, handleFormReset)

      return () => {
        window.removeEventListener(EVENT_KEYS.BUILDER_FORM_RESET, handleFormReset)
      }
    }, [component._id])

    return (
      <>
        {children({
          formId: component._id,
          settings,
          formState,
          setFormState,
          formErrors,
          setFormErrors,
          requiredFieldsStatus,
          setRequiredFieldsStatus,
          dataSource,
          dataSourceTable,
        })}

        {showDebug && (
          <details className='mt-2 py-1'>
            <summary className='cursor-pointer text-m-sm'>
              <code>Debug Value</code>
            </summary>
            <pre
              className='text-[10px] max-w-[90vw] mx-auto overflow-x-auto p-3 rounded-sm'
              style={{
                backgroundColor: 'var(--md-sys-color-outline-variant)',
              }}
            >
              {JSON.stringify(formattedValues, null, 2)}
            </pre>
          </details>
        )}
      </>
    )
  }
)

CompFormCore.displayName = 'CompFormCore'
